import * as Keychain from 'react-native-keychain';
import { Platform } from 'react-native';

const BIOMETRIC_KEYCHAIN_SERVICE = 'one-app-biometric';

export type BiometryType = 'FaceID' | 'TouchID' | 'Fingerprint' | null;

/**
 * Get the supported biometry type on this device
 */
export const getSupportedBiometryType = async (): Promise<BiometryType> => {
  try {
    const biometryType = await Keychain.getSupportedBiometryType();
    if (!biometryType) {
      return null;
    }

    switch (biometryType) {
      case Keychain.BIOMETRY_TYPE.FACE_ID:
        return 'FaceID';
      case Keychain.BIOMETRY_TYPE.TOUCH_ID:
        return 'TouchID';
      case Keychain.BIOMETRY_TYPE.FINGERPRINT:
        return 'Fingerprint';
      case Keychain.BIOMETRY_TYPE.FACE:
        return 'FaceID';
      case Keychain.BIOMETRY_TYPE.IRIS:
        return 'Fingerprint'; // Treat iris as fingerprint category
      default:
        return null;
    }
  } catch (error) {
    console.error('Error getting biometry type:', error);
    return null;
  }
};

/**
 * Get a user-friendly name for the biometry type
 */
export const getBiometryDisplayName = (type: BiometryType): string => {
  switch (type) {
    case 'FaceID':
      return 'Face ID';
    case 'TouchID':
      return 'Touch ID';
    case 'Fingerprint':
      return 'Fingerprint';
    default:
      return 'Biometrics';
  }
};

/**
 * Check if biometric credentials are stored
 */
export const isBiometricsEnabled = async (): Promise<boolean> => {
  try {
    const credentials = await Keychain.getGenericPassword({
      service: BIOMETRIC_KEYCHAIN_SERVICE,
      // Don't prompt for biometrics, just check if credentials exist
      authenticationPrompt: {
        title: 'Check credentials',
      },
    });
    return credentials !== false;
  } catch (error) {
    // If we get an authentication error, credentials exist but weren't unlocked
    // This is expected when just checking if they exist
    const errorMessage = (error as Error).message || '';
    if (
      errorMessage.includes('User canceled') ||
      errorMessage.includes('authentication') ||
      errorMessage.includes('Authentication')
    ) {
      return true;
    }
    return false;
  }
};

/**
 * Check if biometric credentials exist without prompting
 * Note: This will trigger biometric prompt on iOS, so we use a flag to track enrollment
 */
export const hasBiometricCredentials = async (): Promise<boolean> => {
  try {
    // Check if we have stored a flag indicating biometrics are enabled
    // We use a separate non-biometric keychain entry as a flag
    const flag = await Keychain.getGenericPassword({
      service: BIOMETRIC_KEYCHAIN_SERVICE + '-enabled',
    });
    return flag !== false;
  } catch (error) {
    console.error('Error checking biometric credentials:', error);
    return false;
  }
};

/**
 * Store credentials with biometric protection
 */
export const storeBiometricCredentials = async (
  login: string,
  accessToken: string,
): Promise<boolean> => {
  try {
    // Store the actual credentials with biometric protection
    await Keychain.setGenericPassword(login, accessToken, {
      service: BIOMETRIC_KEYCHAIN_SERVICE,
      accessControl: Keychain.ACCESS_CONTROL.BIOMETRY_CURRENT_SET,
      accessible: Keychain.ACCESSIBLE.WHEN_PASSCODE_SET_THIS_DEVICE_ONLY,
      authenticationType: Keychain.AUTHENTICATION_TYPE.BIOMETRICS,
    });

    // Store a flag to indicate biometrics are enabled (no biometric protection)
    await Keychain.setGenericPassword('enabled', 'true', {
      service: BIOMETRIC_KEYCHAIN_SERVICE + '-enabled',
    });

    console.log('Biometric credentials stored successfully');
    return true;
  } catch (error) {
    console.error('Error storing biometric credentials:', error);
    return false;
  }
};

/**
 * Retrieve credentials using biometric authentication
 */
export const getBiometricCredentials = async (): Promise<{
  login: string;
  accessToken: string;
} | null> => {
  try {
    const biometryType = await getSupportedBiometryType();
    const biometryName = getBiometryDisplayName(biometryType);

    const credentials = await Keychain.getGenericPassword({
      service: BIOMETRIC_KEYCHAIN_SERVICE,
      authenticationPrompt: {
        title: `Login with ${biometryName}`,
        subtitle: 'Authenticate to access your account',
        cancel: 'Cancel',
      },
    });

    if (credentials) {
      return {
        login: credentials.username,
        accessToken: credentials.password,
      };
    }
    return null;
  } catch (error) {
    const errorMessage = (error as Error).message || '';

    // User cancelled authentication
    if (
      errorMessage.includes('User canceled') ||
      errorMessage.includes('cancel') ||
      errorMessage.includes('Cancel')
    ) {
      console.log('Biometric authentication cancelled by user');
      return null;
    }

    // Biometric authentication failed (too many attempts, etc.)
    if (
      errorMessage.includes('authentication') ||
      errorMessage.includes('locked')
    ) {
      console.log('Biometric authentication failed:', errorMessage);
      return null;
    }

    console.error('Error getting biometric credentials:', error);
    return null;
  }
};

/**
 * Clear biometric credentials
 */
export const clearBiometricCredentials = async (): Promise<boolean> => {
  try {
    await Keychain.resetGenericPassword({ service: BIOMETRIC_KEYCHAIN_SERVICE });
    await Keychain.resetGenericPassword({ service: BIOMETRIC_KEYCHAIN_SERVICE + '-enabled' });
    console.log('Biometric credentials cleared');
    return true;
  } catch (error) {
    console.error('Error clearing biometric credentials:', error);
    return false;
  }
};

/**
 * Enable biometric login for the current user.
 * Stores credentials and immediately triggers the system biometric prompt
 * to confirm access. Rolls back if the user cancels or authentication fails.
 */
export const enableBiometrics = async (
  login: string,
  accessToken: string,
): Promise<boolean> => {
  // First check if biometrics are supported
  const biometryType = await getSupportedBiometryType();
  if (!biometryType) {
    console.log('Biometrics not supported on this device');
    return false;
  }

  // Store credentials with biometric protection
  const stored = await storeBiometricCredentials(login, accessToken);
  if (!stored) {
    return false;
  }

  // Immediately read the credentials back to trigger the system biometric
  // confirmation dialog (iOS/Android only prompt on read, not on write).
  const biometryName = getBiometryDisplayName(biometryType);
  try {
    const credentials = await Keychain.getGenericPassword({
      service: BIOMETRIC_KEYCHAIN_SERVICE,
      authenticationPrompt: {
        title: `Enable ${biometryName}`,
        subtitle: 'Confirm to activate biometric authentication',
        cancel: 'Cancel',
      },
    });

    if (credentials) {
      return true;
    }

    // User cancelled or authentication failed — roll back stored credentials
    await clearBiometricCredentials();
    return false;
  } catch (error) {
    console.error('Biometric confirmation failed:', error);
    await clearBiometricCredentials();
    return false;
  }
};

/**
 * Disable biometric login
 */
export const disableBiometrics = async (): Promise<boolean> => {
  return clearBiometricCredentials();
};

/**
 * Check if device supports biometrics
 */
export const isBiometricsSupported = async (): Promise<boolean> => {
  const biometryType = await getSupportedBiometryType();
  return biometryType !== null;
};

/**
 * Trigger biometric authentication (FaceID / TouchID / Fingerprint).
 * Returns true if the user successfully authenticated, false otherwise.
 */
export const authenticateWithBiometrics = async (): Promise<boolean> => {
  try {
    const biometryType = await getSupportedBiometryType();
    if (!biometryType) {
      return false;
    }
    const biometryName = getBiometryDisplayName(biometryType);
    const credentials = await Keychain.getGenericPassword({
      service: BIOMETRIC_KEYCHAIN_SERVICE,
      authenticationPrompt: {
        title: biometryName,
        subtitle: 'Authenticate to view card details',
        cancel: 'Cancel',
      },
    });
    return credentials !== false;
  } catch (error) {
    const msg = (error as Error).message || '';
    if (
      msg.includes('User canceled') ||
      msg.includes('cancel') ||
      msg.includes('Cancel') ||
      msg.includes('authentication') ||
      msg.includes('locked')
    ) {
      return false;
    }
    console.error('authenticateWithBiometrics error:', error);
    return false;
  }
};


